import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import {
  ArrowLeft,
  ArrowRight,
  Plug,
  CircleDot,
  Bluetooth,
  Radar,
  Wifi,
  Lock,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Sparkles,
  Bed,
  Mic,
  MicOff,
  AlertCircle,
  X as XIcon,
} from "lucide-react";
import { useMic } from "../context/MicContext";
import {
  initialize as bleInit,
  scan as bleScan,
  stopScan as bleStopScan,
  connect as bleConnect,
  readMac as bleReadMac,
  scanDeviceWifiNetworks as bleScanDeviceWifi,
  sendWifiCredentials as bleSendWifi,
  disconnect as bleDisconnect,
  getLastDiscoveryDebug as bleGetDiscoveryDebug,
  normalizeMac as normalizeSmartDreamMac,
  WIFI_SERVICE_UUIDS,
} from "../services/bleService";
import { BleDiagnosticsPanel } from "../components/ble/BleDiagnosticsPanel";
import { BACKEND_URL } from "../lib/config";

const STEPS = [
  "intro",
  "power",
  "button",
  "bluetooth",
  "search",
  "wifi",
  "password",
  "connecting",
  "microphone",
  "success",
];

// I MOCK_DEVICES/MOCK_WIFIS sono stati rimossi: la scan ora è reale via BLE
// e l'utente inserisce manualmente le credenziali WiFi.

function Dots({ current }) {
  return (
    <div className="flex items-center justify-center gap-1.5 mt-4">
      {STEPS.map((s, i) => (
        <span
          key={s}
          className={`h-1.5 rounded-full transition-all ${
            i === current
              ? "w-6 bg-[#14c2a1]"
              : i < current
              ? "w-1.5 bg-white/60"
              : "w-1.5 bg-white/15"
          }`}
        />
      ))}
    </div>
  );
}

function StepShell({ icon, title, description, children, footer }) {
  return (
    <div className="flex-1 flex flex-col px-6 pt-6 pb-32 animate-[fadeUp_0.35s_ease-out]">
      <div
        className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
        style={{
          background: "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
          boxShadow: "0 14px 30px -12px rgba(20,194,161,0.55)",
        }}
      >
        {icon}
      </div>
      <h2 className="text-white text-[24px] leading-tight font-semibold tracking-tight text-center">
        {title}
      </h2>
      {description && (
        <p className="mt-2 text-white/70 text-[14px] leading-snug text-center max-w-[320px] mx-auto">
          {description}
        </p>
      )}

      <div className="mt-6 flex-1">{children}</div>

      <div className="mt-6 space-y-3">{footer}</div>
    </div>
  );
}

function PrimaryButton({ children, disabled, onClick, loading, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      className="w-full h-12 rounded-2xl text-white font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
      style={{
        background: "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
        boxShadow: "0 14px 30px -12px rgba(20,194,161,0.55)",
      }}
    >
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full h-11 rounded-2xl border border-white/15 bg-white/5 text-white/85 text-sm font-medium hover:bg-white/10 transition-colors"
    >
      {children}
    </button>
  );
}

export default function AddMattressWizard() {
  const navigate = useNavigate();
  const { addMattress, user, logout } = useAuth();
  const mic = useMic();
  const [stepIdx, setStepIdx] = useState(0);
  const step = STEPS[stepIdx];

  const [btEnabled, setBtEnabled] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [foundDevices, setFoundDevices] = useState([]);
  const [showAllDevices, setShowAllDevices] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  // Stati di errore granulari per scan/connect/write
  const [scanError, setScanError] = useState(null);
  const [pairError, setPairError] = useState(null);
  const [pairProgress, setPairProgress] = useState(""); // testo umano per UI
  // L'SSID è sempre modificabile manualmente: il firmware restituisce al
  // massimo tre reti e la rete dell'utente potrebbe non essere nella lista.
  const [wifiSsid, setWifiSsid] = useState("");
  const [wifiListError, setWifiListError] = useState(null);
  const [handshakeRun, setHandshakeRun] = useState(0);
  const [wifiPassword, setWifiPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [mattressName, setMattressName] = useState("");
  // Handshake firmware SmartDream (dopo la connessione BLE):
  //  - deviceMac: MAC reale (comando {'mac'}\r\n → notify frammentate)
  //  - deviceNetworks: lista aggregata ({'wifi'}\r\n → notify frammentate)
  //  - handshakeState: idle | connecting | reading-mac | fetching-networks | ready | error
  const [deviceMac, setDeviceMac] = useState(null);
  const [deviceNetworks, setDeviceNetworks] = useState([]);
  const [handshakeState, setHandshakeState] = useState("idle");
  const [handshakeError, setHandshakeError] = useState(null);
  // Snapshot completo della GATT discovery per debug in-app durante pairing.
  // Popolato dopo lo scan WiFi (che triggera la discovery interna del bleService).
  const [bleDebug, setBleDebug] = useState(null);
  // Frame notify raw ricevuti dal materasso (anche non parseable): utile per
  // capire cosa il firmware sta davvero mandando durante il pairing.
  const [bleRawFrames, setBleRawFrames] = useState([]);
  // Esito dell'ultimo write {'wifi'}\r\n.
  const [bleWriteInfo, setBleWriteInfo] = useState(null);
  // Dialog "Esci dal pairing" (X in top bar)
  const [showExitDialog, setShowExitDialog] = useState(false);
  // Ref per deduplicare le connect(): se già connessi allo stesso deviceId
  // saltiamo il ble.connect() nativo (che iOS a volte fa fallire con
  // "already connected" oppure resta appeso).
  const connectedDeviceIdRef = useRef(null);
  // Log live visibile in-app (senza Xcode/Safari): storico ultimi 15 eventi.
  const [liveLog, setLiveLog] = useState([]);
  const [bleDiagnosticEvents, setBleDiagnosticEvents] = useState([]);
  const pushLiveLog = (line) =>
    setLiveLog((prev) => [
      ...prev.slice(-14),
      `${new Date().toLocaleTimeString()} — ${line}`,
    ]);

  const recordBleDiagnostic = (event) => {
    setBleDiagnosticEvents((prev) => [...prev.slice(-249), event]);
  };

  const ensureBleConnected = async (deviceId) => {
    if (connectedDeviceIdRef.current === deviceId) {
      return { ok: true, deviceId, alreadyConnected: true };
    }
    const conn = await bleConnect(deviceId, {
      retries: 1,
      timeoutMs: 20000,
      onStatus: recordBleDiagnostic,
      onDisconnected: () => {
        connectedDeviceIdRef.current = null;
        pushLiveLog("⚠ connessione BLE interrotta da iOS/dispositivo");
      },
    });
    if (conn.ok) connectedDeviceIdRef.current = deviceId;
    return conn;
  };

  const cancelBleAndExit = async (destination) => {
    if (
      selectedDevice &&
      (handshakeState === "connecting" ||
        handshakeState === "reading-mac" ||
        handshakeState === "fetching-networks" ||
        handshakeState === "ready")
    ) {
      await bleDisconnect(selectedDevice.deviceId).catch(() => {});
    }
    connectedDeviceIdRef.current = null;
    if (destination === "logout") {
      await logout();
      navigate("/login");
    } else {
      navigate("/");
    }
  };

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => {
    if (stepIdx === 0) navigate(-1);
    else setStepIdx((i) => Math.max(i - 1, 0));
  };

  // Step: search → scan BLE reale via @capacitor-community/bluetooth-le.
  // Su web/preview ritorna `not_native` → mostriamo messaggio chiaro invece
  // di mock devices.
  useEffect(() => {
    if (step !== "search") return;
    let cancelled = false;
    setScanning(true);
    setFoundDevices([]);
    setScanError(null);
    (async () => {
      const init = await bleInit();
      if (cancelled) return;
      if (!init.ok && init.reason === "not_native") {
        setScanError(
          "Scansione Bluetooth disponibile solo nell'app iOS/Android installata."
        );
        setScanning(false);
        return;
      }
      if (!init.ok) {
        setScanError(
          init.reason === "init_failed"
            ? "Permessi Bluetooth non concessi. Vai nelle impostazioni iOS → SmartDream → Bluetooth e abilita l'accesso."
            : "Impossibile inizializzare il Bluetooth."
        );
        setScanning(false);
        return;
      }
      const res = await bleScan({
        timeoutMs: 10000,
        onDevice: (d) => {
          if (cancelled) return;
          setFoundDevices((prev) => {
            const idx = prev.findIndex((p) => p.deviceId === d.deviceId);
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = { ...updated[idx], ...d };
              return updated;
            }
            return [...prev, d];
          });
        },
      });
      if (cancelled) return;
      setScanning(false);
      if (!res.ok && res.reason !== "not_native") {
        setScanError(
          "Scansione fallita. Verifica che il Bluetooth sia attivo e riprova."
        );
      }
    })();
    return () => {
      cancelled = true;
      bleStopScan().catch(() => {});
    };
  }, [step]);

  // Handshake firmware SmartDream: all'ingresso dello step `wifi`, se abbiamo
  // già selezionato un device BLE, eseguiamo:
  //   1) connect BLE
  //   2) write {'mac'}\r\n → buffer notify fino a CRLF
  //   3) write {'wifi'}\r\n → lista aggregata, max 3 tentativi
  // e memorizziamo tutto in stato. La connessione resta aperta per lo
  // step successivo (invio credenziali via STEP C).
  useEffect(() => {
    if (step !== "wifi") return;
    if (!selectedDevice) return;
    if (handshakeState !== "idle" && handshakeState !== "error") return;
    let cancelled = false;
    (async () => {
      try {
        setHandshakeError(null);
        setWifiListError(null);
        setBleDiagnosticEvents([]);
        setHandshakeState("connecting");
        pushLiveLog(`connect() → ${selectedDevice.deviceId}…`);
        const conn = await ensureBleConnected(selectedDevice.deviceId);
        if (cancelled) return;
        if (!conn.ok) {
          pushLiveLog(`✗ connect fallito: ${conn.reason || "unknown"}`);
          setHandshakeError(
            `Impossibile connettersi al dispositivo (${conn.error || conn.reason || "errore sconosciuto"}). Copia il report diagnostico qui sotto.`
          );
          setHandshakeState("error");
          return;
        }
        pushLiveLog(
          conn.alreadyConnected ? "✓ già connesso (skip)" : "✓ connesso"
        );
        // STEP A: leggi MAC address reale del materasso via handshake.
        setHandshakeState("reading-mac");
        pushLiveLog("write {'mac'} + CRLF…");
        const macRes = await bleReadMac(selectedDevice.deviceId, {
          timeoutMs: 5000,
          subscribeDelayMs: 300,
          onStatus: recordBleDiagnostic,
        });
        // Popola bleDebug (anche in caso di errore) per visibilità UI
        try {
          setBleDebug(bleGetDiscoveryDebug(selectedDevice.deviceId));
        } catch {
          /* ignore */
        }
        if (cancelled) return;
        if (!macRes.ok) {
          pushLiveLog(
            `✗ MAC: ${macRes.reason || "error"}${
              macRes.error ? ` · ${macRes.error}` : ""
            }`
          );
          setHandshakeError(
            ["read_mac_timeout", "mac_response_timeout"].includes(macRes.reason)
              ? "Il dispositivo non ha risposto al comando MAC. Vedi il box debug in fondo per verificare characteristic e permessi."
              : `Errore nella scrittura del comando MAC: ${
                  macRes.error || macRes.reason || "errore sconosciuto"
                }`
          );
          setHandshakeState("error");
          return;
        }
        pushLiveLog(`✓ MAC ricevuto: ${macRes.mac}`);
        setDeviceMac(macRes.mac);
        // STEP B: raccogli lista reti WiFi via notify stream.
        setHandshakeState("fetching-networks");
        pushLiveLog("write {'wifi'} + CRLF, max 3 tentativi…");
        const wifiRes = await bleScanDeviceWifi(selectedDevice.deviceId, {
          onNetwork: (net) => {
            if (cancelled) return;
            pushLiveLog(`  · rete: ${net.ssid} (${net.rssi ?? "?"} dBm)`);
            setDeviceNetworks((prev) => {
              const others = prev.filter((n) => n.ssid !== net.ssid);
              return [...others, net].sort(
                (a, b) => (b.rssi ?? -999) - (a.rssi ?? -999)
              );
            });
          },
          onRawFrame: (frame) => {
            if (cancelled) return;
            setBleRawFrames((prev) => [...prev.slice(-79), frame]);
          },
          onWriteAttempt: (info) => {
            if (cancelled) return;
            setBleWriteInfo(info);
          },
          onStatus: recordBleDiagnostic,
        });
        if (cancelled) return;
        if (!wifiRes.ok) {
          if (wifiRes.reason === "wifi_list_incomplete") {
            pushLiveLog("⚠ Elenco reti non completo dopo 3 tentativi");
            setWifiListError("Elenco reti non completo");
            setDeviceNetworks([]);
            setHandshakeState("ready");
            return;
          }
          pushLiveLog(`✗ WiFi list: ${wifiRes.reason || "error"}`);
          setHandshakeError(
            "Il dispositivo non ha inviato la lista reti WiFi. Vedi il box debug in fondo."
          );
          setHandshakeState("error");
          return;
        }
        pushLiveLog(
          `✓ ${wifiRes.networks.length} reti WiFi ricevute dal materasso`
        );
        setDeviceNetworks(wifiRes.networks);
        setHandshakeState("ready");
      } catch (e) {
        if (!cancelled) {
          pushLiveLog(`✗ errore: ${String(e)}`);
          setHandshakeError("Errore imprevisto: " + String(e));
          setHandshakeState("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // NOTE: handshakeState is intentionally omitted from the deps array.
    // It is mutated inside the effect (setHandshakeState) and would otherwise
    // re-trigger the effect mid-flow, causing the cleanup to cancel the
    // async handshake before it can complete. The guard at the top
    // (idle | error only) prevents re-entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedDevice, handshakeRun]);

  // Step: connecting → invio credenziali WiFi + registrazione backend.
  // La connessione BLE è GIÀ APERTA dallo step wifi (handshake). Non ri-connettiamo.
  useEffect(() => {
    if (step !== "connecting") return;
    let cancelled = false;
    setPairError(null);
    setPairProgress("Invio credenziali WiFi al dispositivo…");
    (async () => {
      try {
        const write = await bleSendWifi(selectedDevice.deviceId, {
          ssid: wifiSsid,
          password: wifiPassword,
          onStatus: recordBleDiagnostic,
          onRawFrame: (frame) => {
            if (cancelled) return;
            setBleRawFrames((prev) => [...prev.slice(-79), frame]);
          },
        });
        if (cancelled) return;
        if (!write.ok) {
          setPairError(
            "Il dispositivo non ha accettato le credenziali WiFi. Verifica password e riprova."
          );
          await bleDisconnect(selectedDevice.deviceId);
          return;
        }
        setPairProgress(
          write.outcome === "accepted"
            ? "Credenziali accettate. Registrazione dispositivo…"
            : "Credenziali inviate. Registrazione dispositivo…"
        );
        // Backend e frontend usano 12 cifre esadecimali uppercase senza ':' .
        const macForBackend = normalizeSmartDreamMac(deviceMac);
        if (!macForBackend) {
          setPairError("MAC del materasso non valido. Ripeti il pairing Bluetooth.");
          return;
        }
        try {
          await fetch(
            `${BACKEND_URL}/api/devices/pair`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                mac_address: macForBackend,
                user_id: user?.id || "",
                mattress_name:
                  mattressName ||
                  selectedDevice.name ||
                  "Materasso SmartDream",
              }),
            }
          );
        } catch {
          /* non bloccante: retry queue lato client */
        }

        const id = macForBackend.replace(/[^A-Z0-9]/g, "").slice(0, 12);
        const label =
          mattressName ||
          selectedDevice?.name ||
          "Materasso SmartDream";
        addMattress({ id, label });

        if (cancelled) return;
        await bleDisconnect(selectedDevice.deviceId);
        if (
          mic.permission === "granted" ||
          mic.permission === "denied" ||
          mic.permission === "skipped"
        ) {
          setStepIdx(STEPS.indexOf("success"));
        } else {
          setStepIdx(STEPS.indexOf("microphone"));
        }
      } catch (e) {
        if (!cancelled) {
          setPairError("Errore imprevisto durante il pairing: " + String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step]);

  const canNext = useMemo(() => {
    if (step === "bluetooth") return btEnabled;
    if (step === "search") return !!selectedDevice;
    if (step === "wifi") return wifiSsid.trim().length > 0;
    if (step === "password") return wifiPassword.length >= 4;
    return true;
  }, [step, btEnabled, selectedDevice, wifiSsid, wifiPassword]);

  // Riconosce SmartDream dai service reali/legacy oppure dal nome dispositivo.
  const isLikelySmartDream = (d) => {
    const svcs = Array.isArray(d.advertisedServices)
      ? d.advertisedServices.map((s) => String(s).toLowerCase())
      : [];
    const normalizedCandidates = WIFI_SERVICE_UUIDS.map((s) =>
      String(s).replace(/[{}-]/g, "").toLowerCase()
    );
    if (
      svcs.some((service) =>
        normalizedCandidates.includes(
          String(service).replace(/[{}-]/g, "").toLowerCase()
        )
      )
    )
      return true;
    const name = String(d.name || "").toLowerCase();
    return /smartdream|materass|mattress|dongle/.test(name);
  };

  // Deduplica + filtra + ordina i device per l'utente finale.
  // Regole default (showAllDevices=false):
  //  - Sempre visibili: dispositivi "probabilmente SmartDream"
  //  - Filtrati: device anonimi (nome == deviceId) e con RSSI < -85 dBm
  // showAllDevices=true → mostra tutti.
  // Ordinamento: SmartDream in cima, poi RSSI decrescente.
  const visibleDevices = useMemo(() => {
    const seen = new Map();
    for (const d of foundDevices) {
      const prev = seen.get(d.deviceId);
      if (!prev || (d.rssi ?? -999) > (prev.rssi ?? -999)) {
        seen.set(d.deviceId, d);
      }
    }
    const list = Array.from(seen.values()).map((d) => ({
      ...d,
      _isSmartDream: isLikelySmartDream(d),
      _hasName: d.name && d.name !== d.deviceId,
    }));
    const filtered = showAllDevices
      ? list
      : list.filter(
          (d) =>
            d._isSmartDream ||
            (d._hasName && (d.rssi == null || d.rssi >= -85))
        );
    filtered.sort((a, b) => {
      if (a._isSmartDream && !b._isSmartDream) return -1;
      if (!a._isSmartDream && b._isSmartDream) return 1;
      return (b.rssi ?? -999) - (a.rssi ?? -999);
    });
    return filtered;
  }, [foundDevices, showAllDevices]);

  const hiddenDevicesCount = foundDevices.length - visibleDevices.length;

  return (
    <div className="min-h-screen bg-[#2b3134] flex justify-center">
      <div className="w-full max-w-[430px] min-h-screen flex flex-col relative">
        {/* top bar */}
        <div className="sticky top-0 z-10 h-14 px-4 flex items-center justify-between bg-[#2b3134]/85 backdrop-blur-lg border-b border-white/5">
          <button
            onClick={back}
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white transition-colors"
            aria-label="indietro"
            data-testid="wizard-back-btn"
          >
            <ArrowLeft size={18} />
          </button>
          <Logo />
          <button
            onClick={() => setShowExitDialog(true)}
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white transition-colors"
            aria-label="chiudi pairing"
            data-testid="wizard-close-btn"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Dialog di uscita dal pairing */}
        {showExitDialog && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setShowExitDialog(false)}
            data-testid="wizard-exit-dialog"
          >
            <div
              className="w-full max-w-[400px] rounded-3xl bg-[#1e2427] border border-white/10 p-5 space-y-4 animate-[fadeUp_0.25s_ease-out]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-white font-semibold text-[16px]">
                Vuoi uscire dal pairing?
              </div>
              <div className="text-white/65 text-[13px] leading-snug">
                La configurazione WiFi non verrà salvata. Puoi riprendere il
                pairing in qualsiasi momento dal tuo profilo.
              </div>
              <div className="space-y-2 pt-1">
                <button
                  onClick={() => {
                    setShowExitDialog(false);
                    cancelBleAndExit("home");
                  }}
                  data-testid="wizard-exit-home"
                  className="w-full h-11 rounded-2xl bg-white/10 hover:bg-white/15 text-white text-[14px] font-medium border border-white/10"
                >
                  Torna al mio profilo
                </button>
                <button
                  onClick={() => {
                    setShowExitDialog(false);
                    cancelBleAndExit("logout");
                  }}
                  data-testid="wizard-exit-logout"
                  className="w-full h-11 rounded-2xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 text-[14px] font-medium border border-rose-500/30"
                >
                  Esci e torna al login
                </button>
                <button
                  onClick={() => setShowExitDialog(false)}
                  data-testid="wizard-exit-cancel"
                  className="w-full h-11 rounded-2xl text-white/60 hover:text-white text-[13.5px]"
                >
                  Continua il pairing
                </button>
              </div>
            </div>
          </div>
        )}

        <Dots current={stepIdx} />

        {step === "intro" && (
          <StepShell
            icon={<Bed size={34} className="text-white" />}
            title={`Benvenuto${user?.firstName ? ", " + user.firstName : ""}!`}
            description="Per iniziare, aggiungi il tuo primo materasso SmartDream. Ti guidiamo passo passo."
            footer={
              <>
                <PrimaryButton onClick={next} testId="wizard-intro-next">
                  Aggiungi nuovo materasso <ArrowRight size={16} />
                </PrimaryButton>
              </>
            }
          >
            <div
              className="rounded-3xl p-5 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(155deg, #12b096 0%, #0a8670 55%, #083d37 100%)",
              }}
            >
              <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-white/10 blur-3xl" />
              <div className="flex items-center gap-3 text-white">
                <Sparkles size={16} />
                <span className="text-xs uppercase tracking-[0.2em]">
                  SmartDream Setup
                </span>
              </div>
              <ul className="mt-4 space-y-2.5 text-white/95 text-sm">
                {[
                  "Collega il dongle alla corrente",
                  "Premi il pulsante per la modalità pairing",
                  "Accendi il Bluetooth e trova il dispositivo",
                  "Seleziona la rete WiFi di casa",
                ].map((t, i) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-white/20 text-[11px] flex items-center justify-center">
                      {i + 1}
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </StepShell>
        )}

        {step === "power" && (
          <StepShell
            icon={<Plug size={34} className="text-white" />}
            title="Collega il prodotto alla corrente"
            description="Inserisci la spina del dongle SmartDream in una presa elettrica. Attendi qualche secondo per l'avvio."
            footer={
              <>
                <PrimaryButton onClick={next} testId="wizard-power-next">
                  Fatto, avanti <ArrowRight size={16} />
                </PrimaryButton>
                <GhostButton onClick={back} testId="wizard-power-back">Indietro</GhostButton>
              </>
            }
          >
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 flex flex-col items-center">
              <div className="relative w-40 h-40 flex items-center justify-center">
                <div className="absolute inset-4 rounded-full bg-[#14c2a1]/10 animate-ping" />
                <div
                  className="relative w-28 h-28 rounded-3xl flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(155deg, #1a2023, #0b1012)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 40px -20px rgba(0,0,0,0.6)",
                  }}
                >
                  <Plug size={42} className="text-[#14c2a1]" />
                </div>
              </div>
              <p className="mt-4 text-white/80 text-[13px] text-center max-w-[260px]">
                Un LED bianco si accenderà quando il dispositivo sarà
                alimentato correttamente.
              </p>
            </div>
          </StepShell>
        )}

        {step === "button" && (
          <StepShell
            icon={<CircleDot size={34} className="text-white" />}
            title="Tieni premuto il pulsante"
            description="Premi il pulsante sul dongle finché non vedi la luce rossa lampeggiare: il dispositivo è in modalità pairing."
            footer={
              <>
                <PrimaryButton onClick={next} testId="wizard-pairing-button-next">
                  Vedo la luce rossa <ArrowRight size={16} />
                </PrimaryButton>
                <GhostButton onClick={back} testId="wizard-pairing-button-back">Indietro</GhostButton>
              </>
            }
          >
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 flex flex-col items-center">
              <div className="relative w-48 h-36">
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background:
                      "linear-gradient(155deg, #1a2023 0%, #0b1012 100%)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 40px -20px rgba(0,0,0,0.6)",
                  }}
                />
                {/* button */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#2a3034] ring-2 ring-white/5 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-[#40474b]" />
                </div>
                {/* LED */}
                <div className="absolute right-4 top-4 flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{
                      background: "#ff2e2e",
                      boxShadow: "0 0 10px 2px rgba(255,46,46,0.7)",
                      animation: "blink 1s steps(2, start) infinite",
                    }}
                  />
                  <span className="text-white/70 text-[10px] uppercase tracking-wider">
                    Pairing
                  </span>
                </div>
              </div>
              <p className="mt-4 text-white/75 text-[13px] text-center max-w-[280px]">
                Mantieni premuto per circa 3 secondi. La spia rossa inizierà a
                lampeggiare.
              </p>
            </div>
          </StepShell>
        )}

        {step === "bluetooth" && (
          <StepShell
            icon={<Bluetooth size={34} className="text-white" />}
            title="Attiva il Bluetooth"
            description="Per trovare il dispositivo, accendi il Bluetooth del telefono se non è già attivo."
            footer={
              <>
                <PrimaryButton
                  onClick={next}
                  disabled={!canNext}
                  testId="wizard-bluetooth-next"
                >
                  Cerca dispositivi <ArrowRight size={16} />
                </PrimaryButton>
                <GhostButton onClick={back} testId="wizard-bluetooth-back">Indietro</GhostButton>
              </>
            }
          >
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00a88a]/20 border border-[#00a88a]/30 flex items-center justify-center text-[#14c2a1]">
                    <Bluetooth size={18} />
                  </div>
                  <div>
                    <div className="text-white text-[15px] font-medium">
                      Bluetooth
                    </div>
                    <div className="text-white/60 text-xs">
                      {btEnabled ? "Attivo" : "Disattivato"}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setBtEnabled((v) => !v)}
                  data-testid="wizard-bluetooth-toggle"
                  className={`relative w-12 h-7 rounded-full transition-colors ${
                    btEnabled ? "bg-[#14c2a1]" : "bg-white/15"
                  }`}
                  aria-label="toggle bluetooth"
                >
                  <span
                    className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform"
                    style={{
                      transform: btEnabled ? "translateX(20px)" : "translateX(0)",
                    }}
                  />
                </button>
              </div>
              {!btEnabled && (
                <p className="mt-4 text-amber-200/80 text-[12px]">
                  Abilita il Bluetooth per continuare.
                </p>
              )}
            </div>
          </StepShell>
        )}

        {step === "search" && (
          <StepShell
            icon={<Radar size={34} className="text-white" />}
            title="Dispositivi nelle vicinanze"
            description="Mostriamo tutti i dispositivi Bluetooth rilevati. Seleziona il tuo SmartDream dalla lista."
            footer={
              <>
                <PrimaryButton
                  onClick={next}
                  disabled={!canNext}
                  testId="wizard-device-next"
                >
                  Continua con il dispositivo <ArrowRight size={16} />
                </PrimaryButton>
                <GhostButton
                  testId="wizard-device-rescan"
                  onClick={() => {
                    setSelectedDevice(null);
                    setFoundDevices([]);
                    setScanError(null);
                    // Forziamo un re-mount dell'effect cambiando step → search
                    setStepIdx(STEPS.indexOf("bluetooth"));
                    setTimeout(() => setStepIdx(STEPS.indexOf("search")), 50);
                  }}
                >
                  Ricerca di nuovo
                </GhostButton>
              </>
            }
          >
            <div className="relative h-40 mb-3 flex items-center justify-center">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute rounded-full border border-[#14c2a1]/40"
                  style={{
                    width: 80 + i * 50,
                    height: 80 + i * 50,
                    animation: `pulseRing 2.4s ${i * 0.4}s ease-out infinite`,
                  }}
                />
              ))}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
                  boxShadow: "0 12px 28px -12px rgba(20,194,161,0.55)",
                }}
              >
                <Bluetooth size={26} className="text-white" />
              </div>
            </div>

            <div className="space-y-2" data-testid="ble-scan-results">
              {visibleDevices.map((d) => {
                const active = selectedDevice?.deviceId === d.deviceId;
                return (
                  <button
                    key={d.deviceId}
                    onClick={() => setSelectedDevice(d)}
                    data-testid={`ble-device-${d.deviceId}`}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-all text-left animate-[fadeUp_0.3s_ease-out] ${
                      active
                        ? "bg-[#14c2a1]/15 border-[#14c2a1]"
                        : d._isSmartDream
                        ? "bg-[#14c2a1]/8 border-[#14c2a1]/40 hover:bg-[#14c2a1]/15"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          active
                            ? "bg-[#14c2a1] text-[#0b3a34]"
                            : d._isSmartDream
                            ? "bg-[#14c2a1] text-[#0b3a34]"
                            : "bg-white/10 text-white/80"
                        }`}
                      >
                        <Bed size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="text-white text-sm font-medium truncate">
                            {d.name}
                          </div>
                          {d._isSmartDream && (
                            <span className="shrink-0 text-[9.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#14c2a1] text-[#0b3a34]">
                              SmartDream
                            </span>
                          )}
                        </div>
                        <div className="text-white/60 text-[11px] truncate">
                          {d.rssi != null ? `Segnale ${d.rssi} dBm` : "Segnale —"}
                        </div>
                      </div>
                    </div>
                    {active && (
                      <CheckCircle2 size={18} className="text-[#14c2a1] shrink-0" />
                    )}
                  </button>
                );
              })}

              {/* Toggle "Mostra tutti i dispositivi" — visibile se ci sono
                  device nascosti O se siamo già in modalità expanded (per
                  permettere di ricomprimere). */}
              {!scanning && (hiddenDevicesCount > 0 || showAllDevices) && (
                <button
                  onClick={() => setShowAllDevices((v) => !v)}
                  data-testid="ble-toggle-all"
                  className="w-full text-center text-white/60 hover:text-white text-[12px] py-2.5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                >
                  {showAllDevices
                    ? "Nascondi dispositivi non SmartDream"
                    : `Mostra tutti i dispositivi (+${hiddenDevicesCount} nascosti)`}
                </button>
              )}

              {scanning && (
                <div
                  className="flex items-center gap-2 text-white/60 text-xs px-1 py-2"
                  data-testid="ble-scanning"
                >
                  <Loader2 size={14} className="animate-spin" />
                  Ricerca in corso… (max 10 s)
                </div>
              )}

              {!scanning && visibleDevices.length === 0 && !scanError && (
                <div
                  className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-4 text-center"
                  data-testid="ble-empty"
                >
                  <AlertCircle
                    size={18}
                    className="mx-auto text-amber-300"
                  />
                  <div className="text-white text-[13px] mt-2 font-medium">
                    {foundDevices.length === 0
                      ? "Nessun dispositivo trovato"
                      : "Nessun SmartDream nei dintorni"}
                  </div>
                  <div className="text-white/70 text-[12px] mt-1 leading-snug">
                    {foundDevices.length === 0
                      ? "Assicurati che il dispositivo sia acceso e in modalità pairing (pulsante premuto fino al lampeggio LED)."
                      : `Abbiamo rilevato ${foundDevices.length} dispositivi Bluetooth generici. Assicurati che il tuo SmartDream sia in modalità pairing, oppure tocca "Mostra tutti" qui sotto.`}
                  </div>
                </div>
              )}

              {scanError && (
                <div
                  className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3"
                  data-testid="ble-scan-error"
                >
                  <div className="flex items-start gap-2 text-rose-200 text-[12.5px]">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <div>
                      <div>{scanError}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </StepShell>
        )}

        {step === "wifi" && (
          <StepShell
            icon={<Wifi size={34} className="text-white" />}
            title="Rete WiFi"
            description="Scegli una rete rilevata oppure inserisci manualmente il nome della rete WiFi di casa."
            footer={
              <>
                <PrimaryButton
                  onClick={() => setStepIdx(STEPS.indexOf("password"))}
                  disabled={wifiSsid.trim().length === 0}
                  testId="wizard-wifi-next"
                >
                  Continua <ArrowRight size={16} />
                </PrimaryButton>
                <GhostButton onClick={back} testId="wizard-wifi-back">Indietro</GhostButton>
              </>
            }
          >
            <div className="space-y-3" data-testid="wifi-list">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
                <label
                  htmlFor="wifi-manual-ssid"
                  className="block text-white/70 text-[11px] uppercase tracking-wider"
                >
                  Nome rete WiFi
                </label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Wifi size={15} className="text-white/45 shrink-0" />
                  <input
                    id="wifi-manual-ssid"
                    value={wifiSsid}
                    onChange={(event) => setWifiSsid(event.target.value)}
                    placeholder="Scrivi il nome della rete"
                    autoCapitalize="none"
                    autoCorrect="off"
                    data-testid="wifi-manual-ssid-input"
                    className="min-w-0 flex-1 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none"
                  />
                </div>
                <div className="mt-1.5 text-white/45 text-[10.5px] leading-snug">
                  Puoi scriverlo anche se la rete non compare nell’elenco.
                </div>
              </div>

              {/* Handshake firmware SmartDream: lista aggregata via notify. */}
              {(handshakeState === "connecting" ||
                handshakeState === "reading-mac" ||
                handshakeState === "fetching-networks") && (
                <div
                  className="rounded-2xl border border-[#14c2a1]/30 bg-[#14c2a1]/8 px-4 py-3.5 flex items-start gap-3"
                  data-testid="ble-handshake-loading"
                >
                  <Loader2
                    size={16}
                    className="mt-0.5 text-[#14c2a1] animate-spin shrink-0"
                  />
                  <div className="flex-1 text-white text-[12.5px] leading-snug">
                    <div className="font-medium">
                      {handshakeState === "connecting" &&
                        "Connessione al materasso…"}
                      {handshakeState === "reading-mac" &&
                        "Identificazione del materasso…"}
                      {handshakeState === "fetching-networks" &&
                        "Lettura reti WiFi rilevate dal materasso…"}
                    </div>
                    {handshakeState === "fetching-networks" && (
                      <div className="text-white/60 text-[11.5px] mt-0.5">
                        La scansione può richiedere più tentativi
                        {deviceNetworks.length > 0
                          ? ` · ${deviceNetworks.length} trovate`
                          : "…"}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {handshakeState === "error" && handshakeError && (
                <div
                  className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 space-y-2"
                  data-testid="ble-handshake-error"
                >
                  <div className="flex items-start gap-2 text-amber-200 text-[12.5px] leading-snug">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>{handshakeError}</span>
                  </div>
                  <button
                    onClick={async () => {
                      await bleDisconnect(selectedDevice.deviceId).catch(() => {});
                      setHandshakeError(null);
                      setDeviceNetworks([]);
                      setDeviceMac(null);
                      setBleDebug(null);
                      setBleRawFrames([]);
                      setBleWriteInfo(null);
                      setLiveLog([]);
                      setBleDiagnosticEvents([]);
                      connectedDeviceIdRef.current = null;
                      setHandshakeState("idle");
                      setHandshakeRun((value) => value + 1);
                    }}
                    data-testid="ble-handshake-retry"
                    className="text-[#14c2a1] text-[12.5px] underline"
                  >
                    Riprova
                  </button>
                </div>
              )}

              {/* Pannello log live: senza Xcode/Safari, l'utente vede
                  cosa sta succedendo durante il pairing. */}
              {selectedDevice && liveLog.length > 0 && (
                <div
                  className="rounded-2xl border border-white/10 bg-black/40 p-3"
                  data-testid="ble-live-log"
                >
                  <div className="text-white/50 text-[10.5px] uppercase tracking-wider px-1 mb-1.5">
                    Log pairing (in diretta)
                  </div>
                  <div className="max-h-40 overflow-y-auto text-[10.5px] font-mono text-white/70 space-y-0.5">
                    {liveLog.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                </div>
              )}

              {selectedDevice && bleDiagnosticEvents.length > 0 && (
                <BleDiagnosticsPanel
                  device={selectedDevice}
                  handshakeState={handshakeState}
                  error={handshakeError}
                  events={bleDiagnosticEvents}
                />
              )}

              {handshakeState === "ready" && deviceNetworks.length > 0 && (
                <div className="rounded-3xl border border-[#14c2a1]/30 bg-[#14c2a1]/6 p-3 space-y-1.5">
                  <div className="px-2 pt-1 pb-2 text-[#a5f3e0] text-[11px] uppercase tracking-wider flex items-center gap-1.5">
                    <Wifi size={11} />
                    Reti rilevate dal materasso
                  </div>
                  {deviceNetworks.map((n) => {
                    const active = wifiSsid === n.ssid;
                    const bars =
                      n.rssi == null
                        ? 0
                        : n.rssi >= -55
                        ? 4
                        : n.rssi >= -70
                        ? 3
                        : n.rssi >= -80
                        ? 2
                        : 1;
                    return (
                      <button
                        key={n.ssid}
                        onClick={() => setWifiSsid(n.ssid)}
                        data-testid={`wifi-device-${n.ssid}`}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl border transition-all text-left ${
                          active
                            ? "bg-[#14c2a1]/15 border-[#14c2a1]"
                            : "bg-white/5 border-white/10 hover:bg-white/10"
                        }`}
                      >
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                            active
                              ? "bg-[#14c2a1] text-[#0b3a34]"
                              : "bg-white/10 text-white/80"
                          }`}
                        >
                          <Wifi size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-white text-sm font-medium truncate">
                            {n.ssid}
                          </div>
                          <div className="text-white/60 text-[11px] truncate flex items-center gap-1.5">
                            {n.security && n.security !== "OPEN" && (
                              <Lock size={9} className="opacity-70" />
                            )}
                            {n.security || "Aperta"}
                            {n.rssi != null && (
                              <span className="opacity-70">
                                {" · "}
                                {n.rssi} dBm
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-end gap-[2px] h-3 shrink-0">
                          {[1, 2, 3, 4].map((i) => (
                            <span
                              key={i}
                              style={{ height: 3 + i * 2 }}
                              className={`w-[3px] rounded-sm ${
                                i <= bars ? "bg-white" : "bg-white/20"
                              }`}
                            />
                          ))}
                        </div>
                        {active && (
                          <CheckCircle2
                            size={18}
                            className="text-[#14c2a1] shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {handshakeState === "ready" && wifiListError && (
                <div
                  className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-4 text-center"
                  data-testid="wifi-list-incomplete"
                >
                  <AlertCircle size={18} className="mx-auto text-amber-300" />
                  <div className="text-white text-[13px] mt-2 font-medium">
                    Elenco reti non completo
                  </div>
                  <div className="text-white/70 text-[12px] mt-1 leading-snug">
                    Alcuni frammenti Bluetooth non sono arrivati. Puoi riprovare
                    oppure inserire manualmente il nome della rete qui sopra.
                  </div>
                  <button
                    onClick={() => {
                      setWifiListError(null);
                      setDeviceNetworks([]);
                      setBleRawFrames([]);
                      setHandshakeState("idle");
                      setHandshakeRun((value) => value + 1);
                    }}
                    className="mt-3 text-[#14c2a1] text-[12.5px] underline"
                    data-testid="wifi-list-incomplete-retry"
                  >
                    Riprova
                  </button>
                </div>
              )}

              {/* Empty state: handshake completato ma nessuna rete visibile
                  dal materasso. */}
              {handshakeState === "ready" &&
                !wifiListError &&
                deviceNetworks.length === 0 && (
                <div
                  className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-4 text-center"
                  data-testid="wifi-empty-from-device"
                >
                  <AlertCircle
                    size={18}
                    className="mx-auto text-amber-300"
                  />
                  <div className="text-white text-[13px] mt-2 font-medium">
                    Nessuna rete WiFi rilevata dal materasso
                  </div>
                  <div className="text-white/70 text-[12px] mt-1 leading-snug">
                    Assicurati che il router WiFi di casa sia acceso e a
                    portata del materasso, poi tocca &quot;Riprova&quot;.
                  </div>
                  <button
                    onClick={() => {
                      setDeviceNetworks([]);
                      setHandshakeState("idle");
                      setHandshakeRun((value) => value + 1);
                    }}
                    className="mt-3 text-[#14c2a1] text-[12.5px] underline"
                    data-testid="wifi-empty-retry"
                  >
                    Riprova
                  </button>
                </div>
              )}

              {/* Box debug GATT — visibile durante il pairing su device reale.
                  Mostra tutti i service/characteristic scoperti + quello scelto
                  per la write. Rimuovibile dopo che il pairing funziona. */}
              {bleDebug && (
                <div
                  className="rounded-2xl border border-white/10 bg-black/40 px-3 py-3 text-[11px] leading-relaxed font-mono text-white/75 space-y-1.5"
                  data-testid="ble-gatt-debug"
                >
                  <div className="text-white/90 text-[12px] font-sans font-semibold flex items-center gap-2 mb-1">
                    <AlertCircle size={12} />
                    Debug BLE (servizi rilevati)
                  </div>
                  <div>
                    <span className="text-white/50">Chosen service:</span>{" "}
                    <span className="text-[#14c2a1] break-all">
                      {bleDebug.chosen?.serviceUuid || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-white/50">Chosen char:</span>{" "}
                    <span className="text-[#14c2a1] break-all">
                      {bleDebug.chosen?.characteristicUuid || "—"}
                    </span>
                  </div>
                  <div className="text-white/50">
                    match score: {bleDebug.chosen?.matchScore ?? "—"} · notify:{" "}
                    {String(bleDebug.chosen?.canNotify)} · indicate:{" "}
                    {String(bleDebug.chosen?.canIndicate)} · write:{" "}
                    {String(bleDebug.chosen?.canWriteWithResponse)} · writeWwR:{" "}
                    {String(bleDebug.chosen?.canWriteWithoutResponse)} · fallback:{" "}
                    {String(bleDebug.chosen?.fallbackServiceAsChar)} · dynamic:{" "}
                    {String(bleDebug.chosen?.dynamicDiscovery)}
                  </div>
                  <div className="mt-1.5 pt-1.5 border-t border-white/10 text-white/50">
                    Tutti i servizi ({bleDebug.services?.length ?? 0}):
                  </div>
                  {(bleDebug.services || []).map((svc, si) => (
                    <div
                      key={si}
                      className="pl-1.5 border-l border-white/10 space-y-0.5"
                    >
                      <div className="text-white/80 break-all">
                        {svc.uuid}
                      </div>
                      {(svc.characteristics || []).map((c, ci) => {
                        const p = c.properties || {};
                        const flags = [
                          p.read && "R",
                          p.write && "W",
                          p.writeWithoutResponse && "WwR",
                          p.notify && "N",
                          p.indicate && "I",
                        ]
                          .filter(Boolean)
                          .join(",");
                        const isChosen =
                          c.uuid === bleDebug.chosen?.characteristicUuid &&
                          svc.uuid === bleDebug.chosen?.serviceUuid;
                        return (
                          <div
                            key={ci}
                            className={`pl-3 break-all ${
                              isChosen ? "text-[#14c2a1]" : "text-white/55"
                            }`}
                          >
                            {isChosen ? "▶ " : "  "}
                            {c.uuid} [{flags || "—"}]
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
              {/* Box notify raw — ogni frame ricevuto dal materasso, anche
                  non-JSON. Utile per capire cosa il firmware sta davvero
                  mandando (o non mandando). */}
              {(bleRawFrames.length > 0 || bleWriteInfo) && (
                <div
                  className="rounded-2xl border border-white/10 bg-black/40 px-3 py-3 text-[11px] leading-relaxed font-mono text-white/75 space-y-1"
                  data-testid="ble-raw-notify-debug"
                >
                  <div className="text-white/90 text-[12px] font-sans font-semibold flex items-center gap-2 mb-1">
                    <AlertCircle size={12} />
                    Debug notify raw ({bleRawFrames.length} frame)
                  </div>
                  {bleWriteInfo && (
                    <div>
                      <span className="text-white/50">write {'{\'wifi\'} + CRLF'}:</span>{" "}
                      <span
                        className={
                          bleWriteInfo.ok
                            ? "text-[#14c2a1]"
                            : "text-rose-300"
                        }
                      >
                        {bleWriteInfo.ok
                          ? `OK (mode=${bleWriteInfo.mode})`
                          : `FAILED: ${bleWriteInfo.error}`}
                      </span>
                      {bleWriteInfo.firstError && (
                        <div className="text-white/50 pl-2">
                          first attempt: {bleWriteInfo.firstError}
                        </div>
                      )}
                    </div>
                  )}
                  {bleRawFrames.length === 0 ? (
                    <div className="text-rose-300">
                      Nessun frame notify ricevuto dal materasso.
                    </div>
                  ) : (
                    bleRawFrames.map((f, i) => (
                      <div key={i} className="break-all">
                        <span className="text-white/40">
                          #{i + 1} · t{f.attempt || "—"} · {f.byteLength ?? 0}B
                        </span>{" "}
                        <span className="text-[#14c2a1]">{f.hex || "—"}</span>
                        <div className="text-white/65">
                          {f.text || "(testo vuoto)"}
                        </div>
                        {f.reconstructedBuffer && (
                          <div className="text-cyan-200/70">
                            buffer: {f.reconstructedBuffer}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </StepShell>
        )}

        {step === "password" && (
          <StepShell
            icon={<Lock size={34} className="text-white" />}
            title="Password WiFi"
            description={`Inserisci la password della rete "${wifiSsid}".`}
            footer={
              <>
                <PrimaryButton
                  onClick={() => setStepIdx(STEPS.indexOf("connecting"))}
                  disabled={!canNext}
                  testId="wizard-password-next"
                >
                  Connetti dispositivo <ArrowRight size={16} />
                </PrimaryButton>
                <GhostButton onClick={back} testId="wizard-password-back">Indietro</GhostButton>
              </>
            }
          >
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
              <label className="block text-white/70 text-[12px] uppercase tracking-wider">
                Password
              </label>
              <div className="flex items-center h-12 px-4 gap-3 rounded-2xl bg-white/5 border border-white/10 focus-within:border-[#14c2a1]">
                <Lock size={16} className="text-white/50" />
                <input
                  type={showPw ? "text" : "password"}
                  value={wifiPassword}
                  onChange={(e) => setWifiPassword(e.target.value)}
                  placeholder="Password della rete WiFi"
                  data-testid="wifi-password-input"
                  autoCapitalize="off"
                  autoCorrect="off"
                  className="flex-1 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="wizard-password-visibility-toggle"
                  className="text-white/60 hover:text-white"
                  aria-label="toggle"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="text-white/45 text-[11.5px] leading-snug">
                Le credenziali vengono inviate direttamente al materasso via
                Bluetooth. SmartDream non le invia ai propri server.
              </div>
            </div>
          </StepShell>
        )}

        {step === "connecting" && (
          <StepShell
            icon={
              pairError ? (
                <AlertCircle size={34} className="text-rose-300" />
              ) : (
                <Loader2 size={34} className="text-white animate-spin" />
              )
            }
            title={pairError ? "Pairing non riuscito" : "Connessione in corso…"}
            description={
              pairError
                ? "Si è verificato un problema durante il pairing del dispositivo."
                : "Stiamo collegando il tuo SmartDream. Non chiudere l'app."
            }
            footer={
              pairError ? (
                <>
                  <PrimaryButton
                    onClick={() => {
                      setPairError(null);
                      setStepIdx(STEPS.indexOf("bluetooth"));
                      setTimeout(
                        () => setStepIdx(STEPS.indexOf("connecting")),
                        100
                      );
                    }}
                    testId="pair-retry-btn"
                  >
                    Riprova
                  </PrimaryButton>
                  <GhostButton
                    onClick={() => setStepIdx(STEPS.indexOf("search"))}
                    testId="pair-change-device-button"
                  >
                    Cambia dispositivo
                  </GhostButton>
                </>
              ) : null
            }
          >
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-5 space-y-3">
              {pairError ? (
                <div
                  className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-rose-200 text-[13px] leading-snug flex items-start gap-2"
                  data-testid="pair-error"
                >
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{pairError}</span>
                </div>
              ) : (
                <div
                  className="flex items-center gap-3 text-white/85 text-sm"
                  data-testid="pair-progress"
                >
                  <Loader2 size={18} className="animate-spin text-white/70" />
                  {pairProgress || "Avvio…"}
                </div>
              )}
              <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
                <label className="text-white/70 text-xs uppercase tracking-wider">
                  Nome del materasso (opzionale)
                </label>
                <input
                  value={mattressName}
                  onChange={(e) => setMattressName(e.target.value)}
                  placeholder="Es. Camera principale lato destro"
                  data-testid="mattress-name-input"
                  className="w-full mt-1 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none"
                />
              </div>
            </div>
            {selectedDevice && bleDiagnosticEvents.length > 0 && (
              <div className="mt-3">
                <BleDiagnosticsPanel
                  device={selectedDevice}
                  handshakeState={
                    pairError ? "credentials-error" : "credentials"
                  }
                  error={pairError}
                  events={bleDiagnosticEvents}
                />
              </div>
            )}
          </StepShell>
        )}

        {step === "microphone" && (
          <StepShell
            icon={<Mic size={34} className="text-white" />}
            title="Attiva il microfono"
            description="Per rilevare tosse, russare e rumori notturni, SmartDream ha bisogno di accedere al microfono solo durante il monitoraggio del sonno."
            footer={
              <>
                <PrimaryButton
                  testId="wizard-microphone-allow"
                  onClick={async () => {
                    const res = await mic.request();
                    if (res === "granted" || res === "denied") {
                      setStepIdx(STEPS.indexOf("success"));
                    }
                  }}
                >
                  Consenti microfono
                </PrimaryButton>
                <GhostButton
                  testId="wizard-microphone-skip"
                  onClick={() => {
                    mic.skip();
                    setStepIdx(STEPS.indexOf("success"));
                  }}
                >
                  Non ora
                </GhostButton>
              </>
            }
          >
            <div
              className="rounded-3xl p-5 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(155deg, #12b096 0%, #0a8670 55%, #083d37 100%)",
              }}
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center">
                  <Sparkles size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-white text-[15px] font-semibold">
                    A cosa serve
                  </div>
                  <ul className="mt-2 space-y-1.5 text-white/90 text-[13px] leading-snug">
                    <li>• Rilevare episodi di tosse durante la notte</li>
                    <li>• Monitorare il russare e la sua intensità</li>
                    <li>• Identificare interruzioni della respirazione</li>
                    <li>• Registrare brevi clip audio per il tuo diario</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/15 flex items-center gap-2 text-white/85 text-[12px]">
                <Lock size={14} /> I dati restano sul tuo dispositivo. Puoi
                revocare il permesso in qualsiasi momento dalle Impostazioni.
              </div>
            </div>

            {mic.permission === "denied" && (
              <div className="mt-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 text-amber-200 text-[12px] p-3 flex items-start gap-2">
                <MicOff size={14} className="mt-0.5" />
                <span>
                  Permesso negato. Puoi attivarlo più tardi dalle impostazioni
                  del browser o dell&apos;app.
                </span>
              </div>
            )}
          </StepShell>
        )}

        {step === "success" && (
          <StepShell
            icon={<CheckCircle2 size={34} className="text-white" />}
            title="Sei al top! 🎉"
            description="Il tuo materasso SmartDream è collegato e pronto a monitorare il tuo riposo."
            footer={
              <>
                <PrimaryButton
                  onClick={() => navigate("/", { replace: true })}
                  testId="wizard-success-home"
                >
                  Vai alla Home
                </PrimaryButton>
                <GhostButton
                  testId="wizard-success-add-another"
                  onClick={() => {
                    // reset wizard to add another
                    setStepIdx(0);
                    setBtEnabled(false);
                    setSelectedDevice(null);
                    setWifiSsid("");
                    setWifiPassword("");
                    setMattressName("");
                  }}
                >
                  Aggiungi un altro materasso
                </GhostButton>
              </>
            }
          >
            <div
              className="rounded-3xl p-6 text-center relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(155deg, #12b096 0%, #0a8670 55%, #083d37 100%)",
                boxShadow: "0 18px 40px -20px rgba(20,194,161,0.5)",
              }}
            >
              <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-white/10 blur-3xl" />
              <div className="text-white/90 text-xs uppercase tracking-[0.2em]">
                Dispositivo collegato
              </div>
              <div className="mt-2 text-white text-[22px] font-semibold tracking-tight">
                {selectedDevice?.name || "SmartDream"}
              </div>
              <div className="mt-2 text-white/80 text-sm">
                Rete: {wifiSsid || "—"}
              </div>
              <div className="mt-4 text-white/90 text-[13px]">
                Hai {(user?.mattresses?.length || 0)} materasso/i attivi.
              </div>
            </div>
          </StepShell>
        )}
      </div>
    </div>
  );
}
